import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableContainer } from '@mui/material'

import { ETranslationNs, IDraftScanRespDto } from 'src/types'
import {
  useCancelScanDraftsRemove,
  useNotification,
  useRemoveScanDrafts,
  useTableItemsCheck,
} from 'src/hooks'
import { TrashTimeoutIcon } from 'src/assets/icons'

import { BodyRow } from './body-row.component'
import { TableHead } from './table-head.component'

interface IProps {
  data?: IDraftScanRespDto[]
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

export const ScanDraftsTable = ({ data, sort, setSort }: IProps) => {
  const [t] = useTranslation()
  const { mutateAsync: cancelRemove } = useCancelScanDraftsRemove()
  const { notify } = useNotification()

  const {
    handleAllItemsCheck,
    handleItemCheck,
    isItemSelected,
    selectedItemsIds,
    setSelectedItemsIds,
    isIndeterminated,
    isAllChecked,
    isAllUnchecked,
  } = useTableItemsCheck({
    items: data ?? [],
  })

  const { mutateAsync } = useRemoveScanDrafts()

  const handleItemsRemove = async () => {
    if (!selectedItemsIds.length) return

    const resp = await mutateAsync(selectedItemsIds)
    if (!resp) return

    notify({
      message: t('items-delete-notification.v', { ns: ETranslationNs.Common }),
      icon: <TrashTimeoutIcon />,
      onButtonClick: async () => {
        await cancelRemove({ ids: selectedItemsIds })
      },
      timeout: 14000,
    })
    setSelectedItemsIds([])
  }

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const activeSort = sort.split('-')[0]
  const sortDirection = sort.split('-')[1] as 'desc' | 'asc'

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead
            isAllUnchecked={isAllUnchecked}
            handleItemsRemove={handleItemsRemove}
            isAllChecked={isAllChecked}
            isIndeterminated={isIndeterminated}
            handleAllItemsCheck={handleAllItemsCheck}
            handleSortChange={handleSortChange}
            activeSort={activeSort}
            sortDirection={sortDirection}
          />
          <TableBody>
            {data?.map((row) => (
              <BodyRow
                isItemSelected={isItemSelected}
                handleItemCheck={handleItemCheck}
                handleInRowClick={() => setSelectedItemsIds((prevIds) => [...prevIds, row.id])}
                item={row}
                key={row.id}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}
